import React, { Suspense } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { PanelLoader } from "@/components/shared/PanelLoader";
import "./RouteTransition.css";

// Fallback shown while a lazy-loaded route chunk is fetching.
// Uses the same PanelLoader as grids for visual consistency.
const RouteSuspenseFallback: React.FC = () => (
  <div className="flex items-center justify-center h-full w-full">
    <PanelLoader message="Loading..." />
  </div>
);

type RouteType =
  | "inspire"
  | "projects"
  | "project"
  | "copilot"
  | "workflow"
  | "timeline"
  | "other";

function getRouteType(pathname: string): RouteType {
  if (pathname === "/" || pathname === "/inspire") return "inspire";
  if (pathname === "/productions") return "projects";
  // Match /productions/:projectId and /productions/:projectId/workflows and /productions/:projectId/assets
  if (pathname.match(/^\/productions\/[^/]+/)) return "project";
  if (pathname === "/copilot") return "copilot";
  if (pathname.includes("/workflows/")) return "workflow";
  if (pathname.includes("/video-editor/")) return "timeline";
  return "other";
}

export const RouteTransition: React.FC = () => {
  const location = useLocation();
  const currentType = getRouteType(location.pathname);

  // The key MUST be a pure function of the current route type. An earlier
  // implementation used `${prevType}-${currentType}` with prevType tracked
  // via a ref updated in a post-mount effect — that flipped the key on the
  // first re-render after any navigation (e.g. when the detail query
  // resolved or entitlements were applied), unmounting and remounting the
  // entire <Outlet /> subtree a beat after the page first appeared.
  //
  // Collapsing /productions and /productions/:projectId under a shared key keeps
  // the project dashboard mounted while the projectId param changes.
  const transitionKey =
    currentType === "projects" || currentType === "project"
      ? "projects-project"
      : currentType;

  return (
    <div
      key={transitionKey}
      className="route-transition-container route-slide-none flex-1 min-h-0 h-full"
    >
      <Suspense fallback={<RouteSuspenseFallback />}>
        <Outlet />
      </Suspense>
    </div>
  );
};
